import { useCallback, useRef, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import type {
  GetMarketplacePropertySearchQueryVariables,
  MarketplacePropertyListFragment,
  MarketplacePropertySearchFragment,
} from '__generated__/athena/graphql';

import { getPropertyList, getPropertyListAndSearch } from './useSearchQueries';

/**
 * Outcome of a full search. `failed` and `superseded` are kept distinct so
 * callers can react to a genuine fetch failure (e.g. an Athena rate-limit /
 * ClientError, which production telemetry shows is a hot path) — surfacing a
 * retry — without mistaking it for a request that a newer search replaced.
 */
export type SearchStatus = 'applied' | 'failed' | 'superseded';

/**
 * Outcome of a pagination request (`loadNextPage`). `superseded` is distinct
 * from `failed` so a page that resolves after a newer full search started is
 * dropped without flagging an error (its results would splice stale listings
 * into a different result set).
 */
export type LoadMoreStatus = 'appended' | 'empty' | 'failed' | 'superseded';

/**
 * Owns the marketplace result set (search + list), the single loading flag, an
 * error flag, and the shared fetch primitive every search entry point uses.
 * Hosts keep their own URL/location bookkeeping and call `runSearch` /
 * `loadNextPage` instead of re-implementing the fetch → apply → loading dance.
 *
 * The result state is seeded once from SSR props (the marketplace page is
 * long-lived and never remounts on navigation), so result-changing flows must
 * go through this hook rather than relying on prop re-hydration.
 */
export const useMarketplaceSearch = (
  initialSearch: MarketplacePropertySearchFragment,
  initialList: MarketplacePropertyListFragment | null,
) => {
  const [propertySearchData, setPropertySearchData] =
    useState<MarketplacePropertySearchFragment>(initialSearch);
  const [propertyListData, setPropertyListData] = useState<MarketplacePropertyListFragment | null>(
    initialList,
  );
  const [isLoading, setIsLoading] = useState(false);
  // Set when a current (non-superseded) request genuinely fails, so the host
  // can show a retry/toast instead of silently leaving stale results on screen.
  const [hasError, setHasError] = useState(false);

  // Every full search shares this primitive, so a slow request (e.g. a viewport
  // pan) can resolve after a newer one (e.g. a saved-search selection). Tag each
  // request with a monotonic id and only let the latest one apply results or
  // release the loading flag, so a stale response can't clobber newer results
  // or hide a spinner a newer request is still showing.
  const latestRequestId = useRef(0);

  const clearError = useCallback(() => setHasError(false), []);

  // Runs a full search (list + aggregate) and applies the results. Returns
  // `applied` when this request is current and succeeded, `failed` when it is
  // current but errored, and `superseded` when a newer request started while it
  // was in flight (so callers can gate follow-up state on `applied` only).
  const runSearch = useCallback(
    async (payload: GetMarketplacePropertySearchQueryVariables): Promise<SearchStatus> => {
      const requestId = (latestRequestId.current += 1);
      setIsLoading(true);
      setHasError(false);
      try {
        const { searchResult, listResult } = await getPropertyListAndSearch(payload);
        if (requestId !== latestRequestId.current) {
          return 'superseded';
        }
        if (searchResult?.marketplace?.propertySearch) {
          setPropertySearchData(searchResult.marketplace.propertySearch);
        }
        setPropertyListData(listResult?.marketplace?.propertyList ?? null);
        return 'applied';
      } catch (e) {
        Sentry.captureException(e);
        if (requestId !== latestRequestId.current) {
          return 'superseded';
        }
        setHasError(true);
        return 'failed';
      } finally {
        // Only the latest request owns the loading flag — an older request
        // resolving late must not clear a spinner a newer one is still showing.
        if (requestId === latestRequestId.current) {
          setIsLoading(false);
        }
      }
    },
    [],
  );

  // Appends the next page of list results. Returns `appended` on success,
  // `empty` when there was nothing to append, `failed` on a fetch error (so the
  // caller can avoid advancing its page counter and can surface a retry), and
  // `superseded` when a newer full search started while the page was in flight.
  const loadNextPage = useCallback(
    async (
      payload: GetMarketplacePropertySearchQueryVariables,
      nextPage: number,
    ): Promise<LoadMoreStatus> => {
      // Pagination belongs to the current search, so it captures (rather than
      // bumps) the request id. `runSearch` bumps the id when a new full search
      // starts, which invalidates an in-flight page so it can't append its
      // stale results onto — and overwrite the count/nextPage of — the newer
      // result set.
      const requestId = latestRequestId.current;
      try {
        const res = await getPropertyList(payload, nextPage);
        if (requestId !== latestRequestId.current) {
          return 'superseded';
        }
        const propertyList = res.marketplace.propertyList;
        if (!propertyList) {
          return 'empty';
        }
        setPropertyListData((prev) => {
          if (prev?.results) {
            return {
              __typename: prev.__typename,
              results: [...prev.results, ...(propertyList.results ?? [])],
              count: propertyList.count ?? 0,
              nextPage: propertyList.nextPage,
            };
          }
          return propertyList;
        });
        return 'appended';
      } catch (e) {
        Sentry.captureException(e);
        if (requestId !== latestRequestId.current) {
          return 'superseded';
        }
        setHasError(true);
        return 'failed';
      }
    },
    [],
  );

  return {
    propertySearchData,
    propertyListData,
    setPropertyListData,
    isLoading,
    setIsLoading,
    hasError,
    clearError,
    runSearch,
    loadNextPage,
  };
};
